import type { Account } from '@limo/database'
import type { OnboardingSupplementaryData } from '@/lib/onboarding'
import { evaluateOnboarding, CATEGORY_LABELS } from '@/lib/onboarding'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { CheckCircle2, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNavigate, useLocation } from 'react-router-dom'

interface Props {
  account: Account | null
  data: OnboardingSupplementaryData | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function OnboardingProgressDialog({ account, data, open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const location = useLocation()

  console.log(data)

  if (!account || !data) return null

  const result = evaluateOnboarding(account, data)

  const grouped = result.steps.reduce(
    (acc, item) => {
      const cat = item.step.category
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(item)
      return acc
    },
    {} as Record<string, typeof result.steps>
  )

  // Determine if we're in impersonation mode by checking the URL
  const impersonationMatch = location.pathname.match(/\/admin\/accounts\/([^/]+)\/view/)
  const basePath = impersonationMatch
    ? `/admin/accounts/${impersonationMatch[1]}/view`
    : '/dashboard'

  const handleNavigate = (href: string) => {
    onOpenChange(false)
    navigate(`${basePath}${href}`)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Onboarding Progress</SheetTitle>
          <SheetDescription>
            Setup progress for{' '}
            <span className="font-medium">{account.name}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-2 mt-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {result.completedSteps.length} of {result.totalSteps} steps completed
            </span>
            <span className="font-medium">{result.percentage}%</span>
          </div>
          <Progress value={result.percentage} className="h-2" />
        </div>

        <div className="space-y-5 mt-6">
          {Object.entries(CATEGORY_LABELS).map(([category, label]) => {
            const items = grouped[category]
            if (!items) return null
            const allDone = items.every((i) => i.completed)
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-medium">{label}</h4>
                  {allDone && (
                    <Badge variant="secondary" className="text-xs">
                      Complete
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {items.map(({ step, completed }) => {
                    const Icon = step.icon
                    return (
                      <button
                        key={step.id}
                        onClick={() => handleNavigate(step.href)}
                        className={cn(
                          'flex items-start gap-3 rounded-md border p-3 w-full text-left cursor-pointer transition-colors',
                          completed
                            ? 'bg-green-50/50 border-green-200 hover:bg-green-50'
                            : 'bg-background border-border hover:bg-muted/50'
                        )}
                      >
                        {completed ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span
                              className={cn(
                                'text-sm font-medium hover:underline',
                                completed ? 'text-green-800' : 'text-foreground'
                              )}
                            >
                              {step.label}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {step.description}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </SheetContent>
    </Sheet>
  )
}
